import React from 'react';
import {
  render,
  RenderOptions,
  NODE_PARAGRAPH,
  MARK_LINK,
  NODE_HEADING,
  NODE_LI,
  NODE_CODEBLOCK,
  MARK_CODE,
  NODE_IMAGE
} from 'storyblok-rich-text-react-renderer';
import merge from 'lodash/merge';
import { RichtextStoryblok } from 'types/storyblokTypes';
import { Typography } from '@mui/material';
import CopyCode from 'components/core/CopyCode';
import styled from '@emotion/styled';
import DynamicLink from './DynamicLink';
import GifWithModal from './GifWithModal';
import VideoResource from './resourceComponents/VideoResource';
import {
  VideoResourceStoryblok,
} from 'types/storyblokTypes';


const Heading = styled(Typography)<{ callingComponent?: string }>(({
  variant,
  callingComponent,
}) => {
  const leftMargin = '0rem';
  // always setting left margin to 0 for headers, IDK why there was this code below before...
  // const leftMargin =
  //   callingComponent !== 'alert' && variant
  //     ? ((parseInt(variant.substring(1)) - 1) * 0.25).toString() + 'rem'
  //     : '0rem';

  const topMargin = callingComponent === 'alert' ? '0rem' : '1rem';
  return {
    margin: `${topMargin} 0 1rem ${leftMargin}`,
  };
});

export default function renderRichText(
  document: RichtextStoryblok,
  options?: RenderOptions,
  continueOrderedList?: boolean,
  callingComponent?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  let liIndex = 1;
  const defaultOptions: RenderOptions = {
    markResolvers: {
      [MARK_LINK]: linkResolver,
      [MARK_CODE]: (children) => {
        return codeResolver(children, callingComponent);
      },
    },
    nodeResolvers: {
      [NODE_HEADING]: (children, level) =>
        headingResolver(children, level, callingComponent),
      [NODE_CODEBLOCK]: codeblockResolver,
      [NODE_LI]: (children) => {
        const content = listItemResolver(
          children,
          continueOrderedList ? liIndex : undefined
        );
        liIndex += 1;
        return content;
      },
      [NODE_PARAGRAPH]: paragraphResolver,
      [NODE_IMAGE]: (children, props) =>
        imageResolver(children, {
          src: props.src,
          alt: props.alt,
          title: props.title,
        }),

    },
    blokResolvers: {
      ['textWithLink']: (props) => <GifWithModal linkText={props.text} gifUrl={props.link} />,
      ['verticalSpacer']: (props) => <div style={{padding:20}} />,
      ['videoResource']: (props) => {
        // description, title, videoUrl, _editable, _uid
        const blok: VideoResourceStoryblok = {
          description: props.description as string | undefined,
          title: props.title as string | undefined,
          videoUrl: props.videoUrl as string | undefined,
          _editable: props._editable as string | undefined,
          _uid: String(props._uid || ''),
          component: 'videoResource',
        };
        return <VideoResource blok={blok} />;
        // return <div {...props}/>
      },
    }
  };
  const mergedOptions = merge(defaultOptions, options);
  return render(document, mergedOptions);
}

const CodeChunk = styled.code({
  background: '#f6f8fa',
  border: '1px solid #d1d9e0',
  borderRadius: '6px',
  padding: '2px 6px',
  fontSize: '85%',
  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
});

function codeResolver(
  children: React.ReactNode,
  callingComponent?: string
): React.ReactElement | null {
  const code = children?.toString() || '';
  if (callingComponent === 'alert' || code.startsWith('--')) {
    return (
      <b>
        <code style={{ margin: '0 0.5rem' }}>{children}</code>
      </b>
    );
  } else if (callingComponent === 'richText') {
    return code ? (
      <CopyCode code={code} />
    ) : (
      <Typography>{children}</Typography>
    );
  }
  // return <Typography>{children}</Typography>;
  return <CodeChunk className="language-yaml">{children}</CodeChunk>;
}

function imageResolver(
  children: React.ReactNode,
  { src, alt, title }: { src?: string; alt?: string; title?: string }
): React.ReactElement {
  return (
    <img
      src={src}
      alt={alt || ''}
      title={title || ''}
      style={{
        maxWidth: '450px',
        width: '100%',
        height: 'auto',
        display: 'block',
        margin: '0',
      }}
    />
  );
}

function paragraphResolver(children: React.ReactNode): React.ReactElement {
  return <Typography>{children}</Typography>;
}

function headingResolver(
  children: React.ReactNode,
  { level }: { level: 1 | 2 | 3 | 4 | 5 | 6 },
  callingComponent?: string
): React.ReactElement {
  const variant = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  return (
    <Heading variant={variant} callingComponent={callingComponent}>
      {children}
    </Heading>
  );
}

function linkResolver(
  children: React.ReactNode,
  props: {
    linktype?: string;
    href?: string;
    target?: string;
    anchor?: string;
    uuid?: string;
  }
): React.ReactElement {
  const muiProps = props
    ? {
      href: props.linktype === 'email' ? `mailto:${props.href}` : props.href,
      target: props.target,
    }
    : {};
  return <DynamicLink muiProps={muiProps} children={children} />;
}

function listItemResolver(
  children: React.ReactNode,
  liIndex?: number
): React.ReactElement {
  return <li value={liIndex}>{children}</li>;
}

const Pre = styled.pre({
  display: 'block',
  overflowX: 'auto',
  padding: '0.5rem',
  color: 'rgb(0,0,0)',
  background: 'rgb(248,248,255)',
});

const Code = styled.code({
  whiteSpace: 'pre',
});

function codeblockResolver(children: React.ReactNode): React.ReactElement {
  return (
    <Pre>
      <Code className="language-yaml">{children}</Code>
    </Pre>
  );
}
